import React, { createContext, useState, useEffect, useCallback } from "react";
import authService from "../services/authService";
import {
  SearchUiStorageError,
  clearSearchUiStorage,
} from "../../../shared/utils/searchUiStorage";

// Create the Auth Context
export const AuthContext = createContext({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  setUser: () => {},
  setIsAuthenticated: () => {},
  login: () => {},
  logout: () => {},
  refreshUser: () => {},
});

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize authentication state
  const initializeAuth = useCallback(async () => {
    try {
      // Check if user has stored token
      if (authService.isAuthenticated()) {
        // Try to get current user data from API to verify token is still valid
        const userData = await authService.getCurrentUser();
        setUser(userData);
        setIsAuthenticated(true);
      } else {
        // No valid authentication found
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.warn("Failed to initialize auth:", error);
      // If token is invalid, clear stored data
      authService.logout();
      try {
        clearSearchUiStorage();
      } catch (cleanupError) {
        console.error(
          "Failed to clear search UI state after auth failure:",
          cleanupError,
        );
      }
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Login function
  const login = useCallback(async (email, password) => {
    try {
      const response = await authService.login(email, password);
      clearSearchUiStorage();
      setUser(response.user);
      setIsAuthenticated(true);
      return response;
    } catch (error) {
      if (error instanceof SearchUiStorageError) {
        try {
          await authService.logout();
        } catch (logoutError) {
          console.warn(
            "Logout after login cleanup failure did not complete:",
            logoutError,
          );
        }
      }
      setUser(null);
      setIsAuthenticated(false);
      throw error;
    }
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    let cleanupError = null;

    try {
      await authService.logout();
    } catch (error) {
      console.warn("Logout API call failed:", error);
    } finally {
      try {
        clearSearchUiStorage();
      } catch (error) {
        cleanupError = error;
        console.error("Failed to clear search UI state during logout:", error);
      }
      setUser(null);
      setIsAuthenticated(false);
    }

    if (cleanupError) {
      throw cleanupError;
    }
  }, []);

  // Refresh user data
  const refreshUser = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const userData = await authService.getCurrentUser();
      setUser(userData);
    } catch (error) {
      console.error("Failed to refresh user data:", error);
      // If refresh fails, user might need to re-authenticate
      await logout();
    }
  }, [isAuthenticated, logout]);

  // Initialize auth on mount
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Listen for logout events from auth service
  useEffect(() => {
    const handleAuthLogout = () => {
      try {
        clearSearchUiStorage();
      } catch (error) {
        console.error(
          "Failed to clear search UI state after auth logout:",
          error,
        );
      }
      setUser(null);
      setIsAuthenticated(false);
    };

    window.addEventListener("auth-logout", handleAuthLogout);

    return () => {
      window.removeEventListener("auth-logout", handleAuthLogout);
    };
  }, []);

  // Periodically refresh user data if authenticated
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(
      () => {
        refreshUser();
      },
      5 * 60 * 1000,
    ); // Refresh every 5 minutes

    return () => clearInterval(interval);
  }, [isAuthenticated, refreshUser]);

  const contextValue = {
    user,
    isAuthenticated,
    isLoading,
    setUser,
    setIsAuthenticated,
    login,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export default AuthContext;
